/**
 * Imports — spec §N, T-6.8. The screen a firm arrives on. Redrawn on the list
 * shell in Wave 15A (C14 §10.6 items 1–2, ADR-0033): the imports so far as a
 * list, and **Import a file** as a dialog.
 *
 * Six kinds, one upload, and a list of what has been imported before. The
 * dialog offers them in the order a firm must actually do them in — clients
 * first, because everything else names a client — and says so rather than
 * leaving a firm to find out from a file of four hundred `NO_CLIENT`
 * rejections.
 *
 * **Uploading never imports.** It queues a dry run, and the firm is taken to
 * the report. That is FR-N1 read as the requirement it is, and it is why there
 * is no "import now" button anywhere on this screen.
 */

import { Alert, Button, Dialog, type BadgeTone, Select, formatDateTime } from '@fiveox/ui'
import { Download, FileUp, Upload } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  statusTone as mapTone,
  useListTable,
} from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type ImportJob,
  type ImportKind,
  type ImportKindInfo,
  type ImportStatus,
  downloadTemplate,
  imports,
  saveBlob,
  upload,
} from '../../lib/api/imports'

/** The order a firm must do them in. Clients first; everything names a client. */
const ORDER: ImportKind[] = ['clients', 'cases', 'invoices', 'documents', 'calendar', 'tags']

const STATUSES: ImportStatus[] = ['queued', 'running', 'previewed', 'committed', 'failed']

/** A job's tone: the status map's, except that a committed import with
 *  refusals is a warning, not a success — which the status alone cannot say. */
export function statusTone(job: ImportJob): BadgeTone {
  if (job.status === 'committed' && job.rejected_rows > 0) return 'warning'
  return mapTone('import', job.status).tone
}

/** A job's words: the map's, except that a running job is *checking* or
 *  *importing* depending on which pass it is. */
export function statusLabel(job: ImportJob): string {
  if (job.status === 'running') return job.mode === 'commit' ? 'Importing' : 'Checking'
  return mapTone('import', job.status).label
}

export function JobStatus({ job, size }: { job: ImportJob; size?: 'sm' | 'md' }) {
  return (
    <StatusBadge
      kind="import"
      status={job.status}
      label={statusLabel(job)}
      tone={statusTone(job)}
      size={size}
    />
  )
}

function ImportDialog({
  open,
  onClose,
  kinds,
  initialKind,
}: {
  open: boolean
  onClose: () => void
  kinds: ImportKindInfo[]
  initialKind: ImportKind
}) {
  const navigate = useNavigate()
  const fileInput = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState<ImportKind>(initialKind)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setKind(initialKind)
    setError(null)
  }, [open, initialKind])

  const ordered = ORDER.map((key) => kinds.find((row) => row.kind === key)).filter(
    (row): row is ImportKindInfo => row !== undefined,
  )
  const chosen = kinds.find((row) => row.kind === kind)

  async function onFile(file: File | undefined) {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const job = await upload(kind, file)
      // Straight to the report. The upload did not import anything, and the
      // next thing the firm has to do is read what it would have.
      navigate(`/imports/${job.id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That file could not be uploaded.')
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  async function onTemplate() {
    try {
      saveBlob(await downloadTemplate(kind), `prodesk-${kind}.csv`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That template could not be downloaded.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Import a file" error={error} width="lg">
      <div className="grid gap-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-start">
        <span className="text-foreground text-sm font-medium sm:pt-2.5">What you’re importing</span>
        <Select
          aria-label="What are you importing?"
          value={kind}
          onChange={(event) => setKind(event.target.value as ImportKind)}
          hint="Start with your clients — every other import names one."
        >
          {ordered.map((row) => (
            <option key={row.kind} value={row.kind}>
              {row.label}
            </option>
          ))}
        </Select>

        {chosen && (
          <>
            <span className="text-foreground text-sm font-medium">The file</span>
            <div className="text-muted-foreground min-w-0 space-y-2 text-sm">
              <p>{chosen.guidance}</p>
              <p>
                <span className="text-foreground font-medium">Columns: </span>
                {chosen.columns.map((column, index) => (
                  <span key={column}>
                    {index > 0 && ', '}
                    <span
                      className={
                        chosen.required.includes(column) ? 'text-foreground font-medium' : undefined
                      }
                    >
                      {column}
                    </span>
                  </span>
                ))}
              </p>
              <p className="text-xs">Columns in bold are the ones every row needs.</p>
              {/* The template is the remedy the "missing columns" refusal names
                  — without it a firm guesses at a column spelling three times. */}
              <Button variant="link" onClick={() => void onTemplate()}>
                <Download size={14} aria-hidden /> Download the template
              </Button>
            </div>
          </>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        className="hidden"
        accept={chosen?.accepts.join(',') ?? '.csv'}
        onChange={(event) => void onFile(event.target.files?.[0])}
      />

      <Alert tone="info" className="mt-5">
        Choosing a file checks it and shows you what it would do. Nothing reaches your records
        until you say so on the next screen.
      </Alert>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => fileInput.current?.click()} loading={busy} loadingText="Uploading…">
          <FileUp size={16} aria-hidden /> Choose a file
        </Button>
      </div>
    </Dialog>
  )
}

export function Imports() {
  const [kinds, setKinds] = useState<ImportKindInfo[]>([])
  const [jobs, setJobs] = useState<ImportJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialog, setDialog] = useState<{ open: boolean; kind: ImportKind }>({
    open: false,
    kind: 'clients',
  })

  useEffect(() => {
    let cancelled = false
    Promise.all([imports.vocabulary(), imports.list()])
      .then(([vocabulary, listing]) => {
        if (cancelled) return
        setKinds(vocabulary.data)
        setJobs(listing.data)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Imports could not be loaded.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const kindLabel = (kind: ImportKind) => kinds.find((row) => row.kind === kind)?.label ?? kind

  const columns = useMemo<ListColumn<ImportJob>[]>(
    () => [
      {
        key: 'filename',
        label: 'File',
        width: 220,
        required: true,
        phone: true,
        sortField: 'filename',
        render: (job) => (
          <span className="block max-w-[11rem] truncate sm:max-w-xs">{job.filename}</span>
        ),
      },
      {
        key: 'kind',
        label: 'What',
        width: 110,
        sortField: 'kind',
        render: (job) => <span className="text-muted-foreground">{kindLabel(job.kind)}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 160,
        phone: true,
        render: (job) => <JobStatus job={job} />,
      },
      {
        key: 'rows',
        label: 'Rows',
        width: 80,
        align: 'right',
        sortField: 'total_rows',
        render: (job) => <span className="tabular-nums">{job.total_rows}</span>,
      },
      {
        key: 'created',
        label: 'Imported',
        width: 90,
        align: 'right',
        sortField: 'created_rows',
        render: (job) => <span className="tabular-nums">{job.created_rows}</span>,
      },
      {
        key: 'rejected',
        label: 'Refused',
        width: 90,
        align: 'right',
        sortField: 'rejected_rows',
        render: (job) => <span className="tabular-nums">{job.rejected_rows}</span>,
      },
      {
        key: 'created_at',
        label: 'Started',
        width: 150,
        sortField: 'created_at',
        render: (job) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {job.created_at ? formatDateTime(job.created_at) : '—'}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kinds],
  )

  const list = useListTable(jobs, {
    columns,
    search: (job) => [job.filename, kindLabel(job.kind)],
    filters: [
      {
        key: 'kind',
        label: 'What',
        allLabel: 'Every kind',
        options: ORDER.map((kind) => ({ value: kind, label: kindLabel(kind) })),
        match: (job, value) => job.kind === value,
      },
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Any status',
        options: STATUSES.map((status) => ({ value: status, label: mapTone('import', status).label })),
        match: (job, value) => job.status === value,
      },
    ],
    initialSort: { field: 'created_at', dir: 'desc' },
  })

  const openDialog = (kind: ImportKind = 'clients') => setDialog({ open: true, kind })

  return (
    <>
      <ListPage
        title="Imports"
        description="Bring your existing client list, open work, unpaid invoices, documents and calendar into ProDesk. Nothing is imported until you have seen what would be."
        primary={
          <Button onClick={() => openDialog()} disabled={loading}>
            <Upload size={16} aria-hidden /> Import a file
          </Button>
        }
        loading={loading}
        error={error}
        isEmpty={list.isEmpty}
        empty={{
          title: 'Nothing imported yet',
          description:
            'Start with your clients — every other import names one. You will see what the file would do before anything is imported.',
          action: (
            <Button onClick={() => openDialog('clients')}>
              <FileUp size={16} aria-hidden /> Import your clients
            </Button>
          ),
        }}
        toolbar={<ListToolbar list={list} placeholder="Search by file name" />}
      >
        <ListTable list={list} rowKey={(job) => job.id} rowHref={(job) => `/imports/${job.id}`} />
      </ListPage>

      <ImportDialog
        open={dialog.open}
        initialKind={dialog.kind}
        kinds={kinds}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
      />
    </>
  )
}
